/**
 * /teams — manage memberships + create new teams.
 *
 * Identity comes from the dashboard-shell auth context; the loader fetches
 * the multi-team list (powers the "My teams" card) and current members in
 * parallel. Members is best-effort: a flaky `/v1/teams/current/members`
 * surfaces inline rather than locking the user out of the page.
 */

import { useLoaderData } from "react-router-dom";

import type { TeamSwitcherTeam } from "@/components/team-switcher";
import { TeamsBrowser } from "@/features/teams/teams-browser";
import { TeamsPageHeader } from "@/features/teams/teams-page-header";
import { ApiError, api } from "@/lib/api";
import type { MyTeamsResponse, TeamMember, TeamMembersResponse, UserSummary } from "@/lib/types";

import { useAuthState } from "../lib/auth";

interface TeamsLoaderData {
  teams: TeamSwitcherTeam[];
  members: TeamMember[] | null;
  membersError: string | null;
}

async function loadMembers(): Promise<{
  members: TeamMember[] | null;
  membersError: string | null;
}> {
  try {
    const body = await api<TeamMembersResponse>("/teams/current/members");
    return { members: body.items, membersError: null };
  } catch (err) {
    if (err instanceof ApiError) {
      return {
        members: null,
        membersError: `Failed to load members (${err.status}): ${err.message}`,
      };
    }
    return {
      members: null,
      membersError: err instanceof Error ? err.message : String(err),
    };
  }
}

export async function teamsLoader(): Promise<TeamsLoaderData> {
  const [teamsBody, members] = await Promise.all([
    api<MyTeamsResponse>("/me/teams"),
    loadMembers(),
  ]);
  return {
    teams: teamsBody.items ?? [],
    ...members,
  };
}

export default function TeamsRoute() {
  const { teams, members, membersError } = useLoaderData() as TeamsLoaderData;
  const auth = useAuthState();
  // ``UserSummary`` includes ``created_at`` but the UI only reads ``id``;
  // pad with an empty string so the structural type lines up without
  // paying for an extra ``/v1/me`` round-trip.
  const meUser: UserSummary = {
    id: auth.user.id,
    email: auth.user.email,
    name: auth.user.name,
    created_at: "",
  };
  return (
    <section className="space-y-4">
      <TeamsPageHeader />
      <TeamsBrowser
        me={meUser}
        activeTeamId={auth.tenant.team_id}
        activeTeamName={auth.tenant.team_name}
        isAdmin={auth.tenant.role === "team_admin"}
        initialTeams={teams}
        initialMembers={members}
        initialMembersError={membersError}
      />
    </section>
  );
}
